(() => {
  const ROOT = '[data-net-reference-header]';
  const FREE_SHIPPING = 9900;

  function formatMoney(cents) {
    if (window.NetReferenceHeaderMoney) return window.NetReferenceHeaderMoney(cents);
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }
  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#039;','"':'&quot;'}[c]));
  }

  function initCart(header) {
    if (header.dataset.netCartInit === 'true') return;
    header.dataset.netCartInit = 'true';

    const drawer = header.querySelector('[data-net-cart-drawer]');
    const overlay = header.querySelector('[data-net-cart-overlay]');
    const openBtn = header.querySelector('[data-net-cart-open]');
    const closeBtn = header.querySelector('[data-net-cart-close]');
    const removeAllBtn = header.querySelector('[data-net-cart-remove-all]');
    const cartStatus = header.querySelector('[data-net-cart-status]');
    const lines = header.querySelector('[data-net-cart-lines]');
    const count = header.querySelector('[data-net-cart-count]');
    const subtotal = header.querySelector('[data-net-cart-subtotal]');
    const shippingMessage = header.querySelector('[data-net-shipping-message]');
    const shippingBar = header.querySelector('[data-net-shipping-bar]');
    if (!drawer || !overlay || !openBtn || !lines) return;

    let previousFocus = null;
    let oldBodyPadding = '';
    let busy = false;
    let renderVersion = 0;
    const productPrices = new Map();

    // Ajax cart lines omit the variant's catalog compare-at price.
    async function getProductPrices(handle, currency) {
      const key = `${currency}:${handle}`;
      if (!productPrices.has(key)) {
        const rootPath = window.Shopify?.routes?.root || '/';
        const request = fetch(`${rootPath}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' }, credentials: 'same-origin',
        }).then(response => {
          if (!response.ok) throw new Error('product price fetch failed');
          return response.json();
        }).catch(error => {
          productPrices.delete(key);
          throw error;
        });
        productPrices.set(key, request);
      }
      return productPrices.get(key);
    }

    function renderPrice(item, compareAt = 0) {
      const finalPrice = Number(item.final_line_price ?? item.line_price ?? 0);
      const regularPrice = Math.max(Number(compareAt || 0) * item.quantity, Number(item.original_line_price || 0));
      const discounted = regularPrice > finalPrice;
      return `<span class="visually-hidden">${escapeHtml(discounted ? lines.dataset.saleLabel : lines.dataset.regularLabel)}</span>
        <span class="net-ref-cart-line__current-price">${escapeHtml(formatMoney(finalPrice))}</span>
        ${discounted ? `<span class="visually-hidden">${escapeHtml(lines.dataset.regularLabel)}</span><s class="net-ref-cart-line__compare-price">${escapeHtml(formatMoney(regularPrice))}</s>` : ''}`;
    }

    function setPageLock(locked) {
      const pageWrapper = document.querySelector('.page-wrapper');
      pageWrapper?.classList.remove('page-wrapper--drawer-open');
      if (locked) {
        const scrollBar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        oldBodyPadding = document.body.style.paddingRight;
        if (scrollBar) document.body.style.paddingRight = `${scrollBar}px`;
        document.body.classList.add('net-ref-cart-lock');
      } else {
        document.body.classList.remove('net-ref-cart-lock');
        document.body.style.paddingRight = oldBodyPadding;
      }
    }

    function openDrawer() {
      if (header.dataset.pageType === 'cart') return;
      previousFocus = document.activeElement;
      overlay.hidden = false;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      setPageLock(true);
      requestAnimationFrame(() => overlay.classList.add('is-open'));
      closeBtn?.focus({ preventScroll: true });
      refreshCart();
    }

    function closeDrawer() {
      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      setPageLock(false);
      window.setTimeout(() => { if (!drawer.classList.contains('is-open')) overlay.hidden = true; }, 210);
      previousFocus?.focus?.({ preventScroll: true });
    }

    function renderCart(cart) {
      if (!cart) return;
      const version = ++renderVersion;
      if (removeAllBtn) removeAllBtn.hidden = !cart.items?.length;
      count.textContent = cart.item_count ?? 0;
      subtotal.textContent = formatMoney(cart.total_price || 0);
      const remaining = Math.max(0, FREE_SHIPPING - (cart.total_price || 0));
      const pct = Math.min(100, ((cart.total_price || 0) / FREE_SHIPPING) * 100);
      shippingMessage.innerHTML = remaining > 0
        ? `You're <strong>${escapeHtml(formatMoney(remaining))}</strong> away from <strong>Free Shipping!</strong>`
        : `<strong>You've unlocked Free Shipping!</strong>`;
      shippingBar.style.width = `${pct}%`;

      if (!cart.items?.length) {
        lines.innerHTML = '<div class="net-ref-cart-empty">Your cart is empty.</div>';
        return;
      }

      const renderLine = (item) => {
        const image = item.featured_image?.url || item.image || '';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="net-ref-cart-line__variant">${escapeHtml(item.variant_title)}</div>` : '';
        return `<div class="net-ref-cart-line" data-net-line data-key="${escapeHtml(item.key)}">
          <a class="net-ref-cart-line__image" href="${escapeHtml(item.url || '#')}">${image ? `<img src="${escapeHtml(image)}&width=140" alt="${escapeHtml(item.product_title || item.title)}" width="70" height="70" loading="lazy">` : ''}</a>
          <div class="net-ref-cart-line__body">
            <a class="net-ref-cart-line__title" href="${escapeHtml(item.url || '#')}">${escapeHtml(item.product_title || item.title)}</a>
            ${variant}
            <div class="net-ref-cart-line__controls">
              <div class="net-ref-cart-qty">
                <button type="button" data-net-qty="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-net-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
              <button class="net-ref-cart-remove" type="button" data-net-remove>Remove</button>
            </div>
          </div>
          <div class="net-ref-cart-line__price">${renderPrice(item)}</div>
        </div>`;
      };

      const groups = [];
      const bundles = new Map();
      // Once a product has a pack, later one-time additions belong to that pack too.
      const packProducts = new Set(cart.items
        .filter(item => item.properties?._net_bundle_id && !item.selling_plan_allocation)
        .map(item => item.product_id));
      for (const item of cart.items) {
        const bundleId = !item.selling_plan_allocation && packProducts.has(item.product_id)
          ? String(item.product_id) : null;
        if (!bundleId) {
          groups.push({ items: [item] });
          continue;
        }
        let group = bundles.get(bundleId);
        if (!group) {
          group = { bundleId, size: 0, items: [] };
          bundles.set(bundleId, group);
          groups.push(group);
        }
        group.items.push(item);
        group.size += item.quantity;
      }
      lines.innerHTML = groups.map((group) => {
        const contents = group.items.map(renderLine).join('');
        if (!group.bundleId) return contents;
        const heading = (lines.dataset.packHeading || '').replace('[size]', String(group.size || ''));
        return `<section class="net-ref-cart-pack" aria-label="${escapeHtml(heading)}">
          <h3 class="net-ref-cart-pack__heading">${escapeHtml(heading)}</h3>
          ${contents}
        </section>`;
      }).join('');

      for (const item of cart.items) {
        if (!item.handle) continue;
        getProductPrices(item.handle, cart.currency).then(product => {
          if (version !== renderVersion || !header.isConnected) return;
          const variant = product.variants?.find(variant => String(variant.id) === String(item.variant_id));
          if (!variant) return;
          const row = [...lines.querySelectorAll('[data-net-line]')].find(row => row.dataset.key === item.key);
          const price = row?.querySelector('.net-ref-cart-line__price');
          if (price) price.innerHTML = renderPrice(item, variant.compare_at_price);
        }).catch(error => console.warn('[reference-header] Could not load compare-at price', error));
      }
    }

    async function refreshCart() {
      try {
        const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const res = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!res.ok) throw new Error('cart fetch failed');
        renderCart(await res.json());
      } catch (err) {
        console.warn('[reference-header] Could not refresh cart', err);
      }
    }

    async function mutateCart(endpoint, body, clearing = false) {
      if (busy) return;
      busy = true;
      if (removeAllBtn) removeAllBtn.disabled = true;
      if (cartStatus) cartStatus.textContent = clearing ? 'Removing all items…' : '';
      lines.setAttribute('aria-busy', 'true');
      lines.style.opacity = '.55';
      try {
        const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const res = await fetch(`${rootPath}cart/${endpoint}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error('cart change failed');
        const cart = await res.json();
        renderCart(cart);
        if (clearing) {
          if (cartStatus) cartStatus.textContent = 'All items removed. Your cart is empty.';
          closeBtn?.focus({ preventScroll: true });
        }
        document.dispatchEvent(new CustomEvent('net-reference-cart:updated', { detail: { cart } }));
      } catch (err) {
        console.warn('[reference-header] Could not update cart', err);
        if (cartStatus) cartStatus.textContent = 'Could not update your cart. Please try again.';
        await refreshCart();
      } finally {
        busy = false;
        if (removeAllBtn) removeAllBtn.disabled = false;
        lines.removeAttribute('aria-busy');
        lines.style.opacity = '';
      }
    }

    function changeLine(key, quantity) {
      return mutateCart('change', { id: key, quantity });
    }

    removeAllBtn?.addEventListener('click', () => mutateCart('clear', {}, true));

    openBtn.addEventListener('click', e => { e.preventDefault(); openDrawer(); });
    closeBtn?.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);
    drawer.addEventListener('click', e => {
      const line = e.target.closest('[data-net-line]');
      if (!line) return;
      const key = line.dataset.key;
      const qtyButton = e.target.closest('[data-net-qty]');
      if (qtyButton) changeLine(key, Number(qtyButton.dataset.netQty));
      if (e.target.closest('[data-net-remove]')) changeLine(key, 0);
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
      if (e.key === 'Tab' && drawer.classList.contains('is-open')) {
        const focusable = [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter(el => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    // Keep the count synced with common Shopify/theme cart update events.
    ['cart:update','cart:updated','cart-lines-update','cart:refresh','product:added'].forEach(name => {
      document.addEventListener(name, () => window.setTimeout(refreshCart, 50));
    });
    document.addEventListener('net-reference-cart:updated', refreshCart);

    // Product forms and quick-add both publish the standard Shopify cart event.
    const handleCartUpdate = event => {
      const sourceModal = event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;
      const onSuccess = ({ detail } = {}) => {
        if (detail?.didError) return;
        refreshCart();
        if (event.action !== 'add' || header.dataset.pageType === 'cart') return;
        // Let quick-add finish closing and restore focus before opening the cart.
        const openAfterModal = () => requestAnimationFrame(openDrawer);
        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAfterModal, { once: true });
        } else {
          openAfterModal();
        }
      };
      if (event.promise && typeof event.promise.then === 'function') {
        event.promise.then(onSuccess).catch(() => {});
      } else {
        onSuccess();
      }
    };
    ['shopify:cart:lines-update', 'cart-lines-update'].forEach(name => {
      document.addEventListener(name, handleCartUpdate);
    });

    ['net-cart:open', 'cart:open'].forEach(name => {
      document.addEventListener(name, () => openDrawer());
    });

    // Initial count is Liquid-rendered; refresh asynchronously for theme-editor/AJAX navigations.
    refreshCart();
  }

  function boot(scope = document) { scope.querySelectorAll?.(ROOT).forEach(initCart); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
